import React, { useState } from 'react';
import { useMilestones } from '../../context/MilestoneContext';
import { Award, Filter, Search, CheckCircle, Clock, Target, PenTool, Trash2, Edit, Share2, X, Info } from 'lucide-react';
import MilestoneCreator from './MilestoneCreator';
import MilestoneDetailsModal from './MilestoneDetailsModal';
import StatCard from '../shared/StatCard';
import { usePlayer } from '../../context/PlayerContext';
import HeaderWithSeasonSelector from '../shared/HeaderWithSeasonSelector';

const MilestonesPage = () => {
  const { 
    getFilteredMilestones, 
    getSortedMilestones, 
    updateMilestone,
    deleteMilestone,
    loading,
    error,
    ACHIEVEMENT_TYPES,
    MILESTONE_CATEGORIES,
    MILESTONE_TYPES
  } = useMilestones();
  
  const { playerData } = usePlayer();
  
  // Filter and sort state
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'achieved', 'in-progress'
  const [filterType, setFilterType] = useState('all'); // 'all', 'batting', 'pitching'
  const [filterCategory, setFilterCategory] = useState('all'); // 'all', 'game', 'season', 'career', 'special'
  const [filterAchievementType, setFilterAchievementType] = useState('all'); // 'all', 'performance', 'streak', etc.
  const [sortBy, setSortBy] = useState('date'); // 'date', 'progress', 'alphabetical'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Get filtered and sorted milestones
  const milestones = getSortedMilestones(
    getFilteredMilestones({
      status: filterStatus,
      type: filterType,
      category: filterCategory,
      achievementType: filterAchievementType,
      search: searchQuery
    }),
    sortBy
  );
  
  // Handle share milestone
  const handleShare = (milestone) => {
    setSelectedMilestone(milestone);
    setShowShareModal(true);
  };
  
  // Handle view milestone details
  const handleViewDetails = (milestone) => {
    setSelectedMilestone(milestone);
    setShowDetailsModal(true);
  };
  
  // Handle delete milestone
  const handleDeleteClick = (milestone) => {
    setSelectedMilestone(milestone);
    setShowDeleteModal(true);
  };
  
  // Confirm delete milestone
  const confirmDelete = async () => {
    if (!selectedMilestone) return;
    
    try {
      await deleteMilestone(selectedMilestone.id);
      setShowDeleteModal(false);
      setSelectedMilestone(null);
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterCategory('all');
    setFilterAchievementType('all');
    setSearchQuery('');
  };
  
  return (
    <div>
      <HeaderWithSeasonSelector
        title="Milestones & Achievements"
        subtitle="Track your baseball achievements"
        actions={[
          { 
            icon: Award, 
            onClick: () => {}, 
            label: 'Milestones'
          }
        ]}
      />
      
      <div className="container mx-auto px-4 py-6">
      
      {/* Milestone Creator */}
      <MilestoneCreator />
      
      {/* Filters and Search */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <div className="relative w-full md:w-auto mb-4 md:mb-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400 dark:text-darkText-tertiary" />
            </div>
            <input
              type="text"
              placeholder="Search milestones..."
              className="pl-10 w-full md:w-64 p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-md text-sm flex items-center ${
                showFilters 
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                  : 'bg-gray-100 text-gray-700 dark:bg-dark-400 dark:text-darkText-secondary'
              }`}
            >
              <Filter size={16} className="mr-1.5" />
              Filters
            </button>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm bg-white dark:bg-dark-400 dark:text-darkText-primary"
            >
              <option value="date">Sort by Date</option>
              <option value="progress">Sort by Progress</option>
              <option value="alphabetical">Sort Alphabetically</option>
            </select>
      </div>
    </div>
  </div>
        
        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-gray-50 dark:bg-dark-300 p-4 rounded-md mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm bg-white dark:bg-dark-400 dark:text-darkText-primary"
              >
                <option value="all">All Status</option>
                <option value="achieved">Achieved</option>
                <option value="in-progress">In Progress</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm bg-white dark:bg-dark-400 dark:text-darkText-primary"
              >
                <option value="all">All Types</option>
                <option value={MILESTONE_TYPES.BATTING}>Batting</option>
                <option value={MILESTONE_TYPES.PITCHING}>Pitching</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm bg-white dark:bg-dark-400 dark:text-darkText-primary"
              >
                <option value="all">All Categories</option>
                <option value={MILESTONE_CATEGORIES.GAME}>Game</option>
                <option value={MILESTONE_CATEGORIES.SEASON}>Season</option>
                <option value={MILESTONE_CATEGORIES.CAREER}>Career</option>
                <option value={MILESTONE_CATEGORIES.SPECIAL}>Special</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Achievement Type</label>
              <select
                value={filterAchievementType}
                onChange={(e) => setFilterAchievementType(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm bg-white dark:bg-dark-400 dark:text-darkText-primary"
              >
                <option value="all">All Achievement Types</option>
                <option value={ACHIEVEMENT_TYPES.PERFORMANCE}>Performance-based</option>
                <option value={ACHIEVEMENT_TYPES.STREAK}>Streak-based</option>
                <option value={ACHIEVEMENT_TYPES.COMPARATIVE}>Comparative</option>
                <option value={ACHIEVEMENT_TYPES.SEASONAL}>Seasonal</option>
                <option value={ACHIEVEMENT_TYPES.CAREER}>Career</option>
              </select>
            </div>
            
            <div className="md:col-span-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="text-blue-600 dark:text-darkText-link text-sm hover:underline"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Milestones List */}
      <div className="space-y-6">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-darkText-secondary">Loading milestones...</p>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md text-red-700 dark:text-red-300">
            <p className="font-medium">Error loading milestones</p>
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && milestones.length === 0 && (
          <div className="text-center py-12 bg-gray-50 dark:bg-dark-300 rounded-lg">
            <Award size={48} className="mx-auto text-gray-300 dark:text-darkText-tertiary mb-4" />
            <h3 className="text-lg font-medium mb-2 dark:text-darkText-primary">No milestones found</h3>
            <p className="text-gray-500 dark:text-darkText-secondary mb-6">
              {searchQuery || filterStatus !== 'all' || filterType !== 'all' || filterCategory !== 'all' || filterAchievementType !== 'all'
                ? "Try adjusting your filters to see more results"
                : "Create your first milestone to start tracking your achievements"}
            </p>
            
            {(searchQuery || filterStatus !== 'all' || filterType !== 'all' || filterCategory !== 'all' || filterAchievementType !== 'all') && (
              <button
                onClick={resetFilters}
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 px-4 py-2 rounded-md text-sm font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
        
        {/* Achieved Milestones */}
        {!loading && !error && milestones.filter(m => m.achieved).length > 0 && (
          <div>
            <h2 className="font-semibold text-lg mb-3 dark:text-darkText-primary flex items-center">
              <CheckCircle size={18} className="mr-2 text-green-500" />
              Achieved
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {milestones.filter(m => m.achieved).map((milestone) => (
                <StatCard 
                  key={milestone.id} 
                  className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 cursor-pointer"
                  onClick={() => handleViewDetails(milestone)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <div className="font-bold text-blue-800 dark:text-blue-300 flex items-center">
                        {milestone.type === MILESTONE_TYPES.BATTING ? 
                          <Target size={16} className="mr-2" /> : 
                          <PenTool size={16} className="mr-2" />
                        }
                        {milestone.title}
                      </div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">{milestone.description}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                          Achieved on {milestone.achievedDate ? new Date(milestone.achievedDate).toLocaleDateString() : 'Unknown date'}
                        </div>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 capitalize">
                          {milestone.category}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(milestone);
                        }}
                        className="text-blue-600 dark:text-blue-400 p-1 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded mr-1"
                      >
                        <Info size={16} />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(milestone);
                        }}
                        className="text-blue-600 dark:text-blue-400 p-1 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded"
                      >
                        <Share2 size={16} />
                      </button>
                      
                      {milestone.createdBy === playerData?.id && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(milestone);
                          }}
                          className="text-red-500 dark:text-red-400 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </StatCard>
              ))}
            </div>
          </div>
        )}
        
        {/* In Progress Milestones */}
        {!loading && !error && milestones.filter(m => !m.achieved).length > 0 && (
          <div className="mt-8">
            <h2 className="font-semibold text-lg mb-3 dark:text-darkText-primary flex items-center">
              <Clock size={18} className="mr-2 text-yellow-500" />
              In Progress
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {milestones.filter(m => !m.achieved).map((milestone) => (
                <StatCard 
                  key={milestone.id}
                  className="cursor-pointer"
                  onClick={() => handleViewDetails(milestone)}
                >
                  <div className="flex justify-between mb-2">
                    <div className="font-bold dark:text-darkText-primary flex items-center">
                      {milestone.type === MILESTONE_TYPES.BATTING ? 
                        <Target size={16} className="mr-2 text-blue-500" /> : 
                        <PenTool size={16} className="mr-2 text-purple-500" />
                      }
                      {milestone.title}
                      <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-400 text-gray-600 dark:text-gray-300 capitalize">
                        {milestone.category}
                      </span>
                    </div>
                    <div className="text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded">
                      In Progress
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 dark:text-darkText-secondary mb-2">
                    {milestone.description}
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-dark-400 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-500 dark:bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${milestone.progress || 0}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between items-center mt-2">
                    <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                      {(milestone.progress || 0).toFixed(0)}% complete
                    </div>
                    
                    <div className="flex items-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(milestone);
                        }}
                        className="text-blue-600 dark:text-blue-400 p-1 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded mr-1"
                      >
                        <Info size={16} />
                      </button>
                      {milestone.createdBy === playerData?.id && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(milestone);
                          }}
                          className="text-red-500 dark:text-red-400 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </StatCard>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Milestone Details Modal */}
      {showDetailsModal && selectedMilestone && (
        <MilestoneDetailsModal 
          milestone={selectedMilestone} 
          onClose={() => setShowDetailsModal(false)} 
        />
      )}
      
      {/* Share Milestone Modal */}
      {showShareModal && selectedMilestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white dark:bg-dark-200 rounded-lg p-4 w-11/12 max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold dark:text-darkText-primary">Share Achievement</h3>
              <button onClick={() => setShowShareModal(false)} className="text-gray-500 dark:text-darkText-secondary">
                <X size={20} />
              </button>
            </div>
            
            <div className="bg-gray-100 dark:bg-dark-300 p-5 rounded-lg mb-4">
              <div className="flex justify-center mb-3">
                <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
                  <Award size={32} className="text-white" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <h4 className="text-center font-bold text-lg dark:text-darkText-primary">{selectedMilestone.title}</h4>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 capitalize">
                  {selectedMilestone.category}
                </span>
              </div>
              <p className="text-center text-gray-700 dark:text-darkText-secondary">{selectedMilestone.description}</p>
              <p className="text-center text-sm text-gray-500 dark:text-darkText-tertiary mt-2">
                Achieved by {playerData?.name || 'Player'}
              </p>
            </div>
            
            <div className="flex flex-col mb-4">
              <p className="text-sm text-gray-600 dark:text-darkText-secondary mb-2">Share your achievement with:</p>
              <div className="grid grid-cols-4 gap-3">
                <button className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center mb-1">
                    <span className="text-white text-lg font-bold">f</span>
                  </div>
                  <span className="text-xs dark:text-darkText-secondary">Facebook</span>
                </button>
                <button className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-sky-500 flex items-center justify-center mb-1">
                    <span className="text-white text-lg font-bold">X</span>
                  </div>
                  <span className="text-xs dark:text-darkText-secondary">Twitter</span>
                </button>
                <button className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <span className="text-xs dark:text-darkText-secondary">WhatsApp</span>
                </button>
                <button className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-dark-400 flex items-center justify-center mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-darkText-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <span className="text-xs dark:text-darkText-secondary">More</span>
                </button>
              </div>
            </div>
            
            <div className="flex justify-end pt-2">
              <button 
                onClick={() => setShowShareModal(false)}
                className="bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white py-2 px-4 rounded"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMilestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white dark:bg-dark-200 rounded-lg p-4 w-11/12 max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold dark:text-darkText-primary">Delete Milestone</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-500 dark:text-darkText-secondary">
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-darkText-secondary mb-2">
                Are you sure you want to delete this milestone?
              </p>
              <p className="text-sm text-gray-500 dark:text-darkText-tertiary">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-dark-400 text-gray-700 dark:text-darkText-secondary rounded-md text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestonesPage;
